import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Plus, Search, Filter, Edit, Trash2, Trophy, Star } from 'lucide-react';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { PERSIAN_TEXTS } from '../../constants/persianTexts';
import rankService from '../../services/rankService';
import { getFullImageUrl } from '../../utils/imageUpload';
import RankModal from '../../components/Modals/RankModal';
import toast from 'react-hot-toast';

const RanksContainer = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
`;

const Title = styled.h1`
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
`;

const RanksList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 30px;
`;

const RankCard = styled(Card)`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 212, 255, 0.15);
    border-color: #00d4ff;
  }
`;

const RankContent = styled.div`
  display: flex;
  align-items: center;
  padding: 20px;
  gap: 20px;
`;

const RankImage = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 12px;
  overflow: hidden;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RankInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RankName = styled.h3`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const RankId = styled.span`
  color: #6c6c8a;
  font-size: 14px;
`;

const RankDetails = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailLabel = styled.span`
  color: #b8b8d1;
  font-size: 12px;
  font-weight: 500;
`;

const DetailValue = styled.span`
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
`;

const RankActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const ActionButton = styled.button`
  background: ${props => props.variant === 'edit' ? 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)' : 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)'};
  border: none;
  border-radius: 8px;
  padding: 8px 12px;
  color: white;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6c6c8a;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 20px;
`;

const EmptyTitle = styled.h3`
  color: #b8b8d1;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 10px 0;
`;

const EmptyDescription = styled.p`
  color: #6c6c8a;
  font-size: 16px;
  margin: 0;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #b8b8d1;
`;

const Ranks = () => {
  const [ranks, setRanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showRankModal, setShowRankModal] = useState(false);
  const [selectedRank, setSelectedRank] = useState(null);

  useEffect(() => {
    fetchRanks();
  }, []);

  const fetchRanks = async () => {
    try {
      setLoading(true);
      const data = await rankService.getRanks();
      setRanks(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching ranks:', error);
      toast.error('خطا در بارگذاری رتبه‌ها');
    } finally {
      setLoading(false);
    }
  };

  const filteredRanks = ranks.filter(rank =>
    rank.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rank.id?.toString().includes(searchTerm)
  );

  const handleAddRank = () => {
    setSelectedRank(null);
    setShowRankModal(true);
  };

  const handleEditRank = (rank) => {
    setSelectedRank(rank);
    setShowRankModal(true);
  };

  const handleDeleteRank = async (rank) => {
    if (window.confirm('آیا از حذف این رتبه اطمینان دارید؟')) {
      try {
        await rankService.deleteRank(rank.id);
        toast.success('رتبه با موفقیت حذف شد');
        fetchRanks();
      } catch (error) {
        toast.error('خطا در حذف رتبه');
        console.error('Error deleting rank:', error);
      }
    }
  };

  const handleRankModalClose = () => {
    setShowRankModal(false);
    setSelectedRank(null);
  };

  const handleRankModalSuccess = () => {
    fetchRanks();
  };

  if (loading) {
    return (
      <RanksContainer>
        <LoadingSpinner>
          <div>در حال بارگذاری...</div>
        </LoadingSpinner>
      </RanksContainer>
    );
  }

  return (
    <RanksContainer>
      <Header>
        <Title>مدیریت رتبه‌ها</Title>
        <SearchContainer>
          <Input
            type="text"
            placeholder="جستجو در رتبه‌ها..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={<Search size={16} />}
          />
          <Button variant="outline" icon={<Filter size={16} />}>
            فیلتر
          </Button>
          <Button variant="primary" onClick={handleAddRank} icon={<Plus size={16} />}>
            افزودن رتبه
          </Button>
        </SearchContainer>
      </Header>

      <RankModal
        show={showRankModal}
        rank={selectedRank}
        onClose={handleRankModalClose}
        onSuccess={handleRankModalSuccess}
      />

      {filteredRanks.length === 0 ? (
        <EmptyState>
          <EmptyIcon>🏆</EmptyIcon>
          <EmptyTitle>هیچ رتبه‌ای یافت نشد</EmptyTitle>
          <EmptyDescription>
            {searchTerm ? 'هیچ رتبه‌ای با این جستجو یافت نشد' : 'هنوز هیچ رتبه‌ای ایجاد نشده است'}
          </EmptyDescription>
        </EmptyState>
      ) : (
        <RanksList>
          {filteredRanks.map((rank) => (
            <motion.div
              key={rank.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <RankCard>
                <RankContent>
                  <RankImage>
                    {rank.picture ? (
                      <img src={getFullImageUrl(rank.picture)} alt={rank.name} />
                    ) : (
                      <Trophy size={32} color="#ffffff" />
                    )}
                  </RankImage>
                  
                  <RankInfo>
                    <RankName>{rank.name}</RankName>
                    <RankId>#{rank.id}</RankId>
                    
                    <RankDetails>
                      <DetailItem>
                        <DetailLabel>امتیاز</DetailLabel>
                        <DetailValue>{rank.minimumScore} - {rank.maximumScore}</DetailValue>
                      </DetailItem>
                      <DetailItem>
                        <DetailLabel>جایزه ارتقا</DetailLabel>
                        <DetailValue>{rank.rankUpPrize} تومان</DetailValue>
                      </DetailItem>
                    </RankDetails>
                  </RankInfo>
                  
                  <RankActions>
                    <ActionButton variant="edit" onClick={() => handleEditRank(rank)}>
                      <Edit size={14} />
                      ویرایش
                    </ActionButton>
                    <ActionButton variant="delete" onClick={() => handleDeleteRank(rank)}>
                      <Trash2 size={14} />
                      حذف
                    </ActionButton>
                  </RankActions>
                </RankContent>
              </RankCard>
            </motion.div>
          ))}
        </RanksList>
      )}
    </RanksContainer>
  );
};

export default Ranks;
